import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { CatalogEntry, CatalogUsage } from '@central/contracts';
import { ChevronDown, ChevronUp, Pencil, Plus, Power, RotateCcw, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRegion, SkeletonText } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { mensagemDeErro } from '@/lib/format';
import { chavesDeAdministracao } from '@/features/admin/admin.api';
// O `Modal` é genérico e nasceu na tela de chamado. Promovê-lo a
// `components/` é a arrumação certa, mas mexe em `features/tickets/`, que
// pertence a outra mudança em andamento — então aqui ele é reutilizado, não
// copiado: duas janelas modais divergem no primeiro ajuste de foco ou de Esc.
import { Modal } from '@/features/tickets/Modal';

/**
 * As peças que as cinco telas de catálogo compartilham.
 *
 * Existem porque os cinco catálogos são a mesma interação com campos
 * diferentes: listar, criar, renomear, reordenar, desativar. Escrever a
 * confirmação de desativação cinco vezes garantiria que em algum lugar ela não
 * mostraria a contagem de chamados — que é justamente a informação que faz a
 * confirmação valer alguma coisa.
 */

// ── casca da tela ──────────────────────────────────────────────────────────

export function CascaDeCatalogo<T>({
  titulo,
  resumo,
  rotuloDoBotao,
  aoCriar,
  consulta,
  vazio,
  children,
}: {
  titulo: string;
  resumo: string;
  rotuloDoBotao: string;
  aoCriar?: () => void;
  consulta: UseQueryResult<{ items: T[] }>;
  vazio: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold tracking-tight">{titulo}</h2>
          <p className="max-w-2xl text-sm text-muted-foreground">{resumo}</p>
        </div>
        {aoCriar ? (
          <Button size="sm" onClick={aoCriar} icon={<Plus className="h-4 w-4" aria-hidden />}>
            {rotuloDoBotao}
          </Button>
        ) : null}
      </header>

      <Card>
        <CardBody className="p-0">
          {consulta.isError ? (
            <div className="px-5 py-6">
              <ErrorState
                error={consulta.error}
                title="Não foi possível carregar o catálogo"
                onRetry={() => void consulta.refetch()}
              />
            </div>
          ) : consulta.isPending ? (
            <SkeletonRegion label="Carregando o catálogo" className="space-y-3 px-5 py-5">
              <SkeletonText lines={5} />
            </SkeletonRegion>
          ) : consulta.data.items.length === 0 ? (
            <div className="px-5 py-14 text-center text-sm text-muted-foreground">{vazio}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">{children}</table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export function Cabecalho({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <th className={cn('px-4 py-3 text-left font-medium', className)}>{children}</th>;
}

export function Celula({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn('px-4 py-3 align-middle', className)}>{children}</td>;
}

export function LinhaDeCabecalho({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-border bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
        {children}
      </tr>
    </thead>
  );
}

/**
 * A linha do registro inativo fica **marcada, não escondida**.
 *
 * Esconder faria o administrador procurar por uma chave que "não existe" e
 * receber 409 ao tentar criá-la de novo — a chave está ocupada por uma linha
 * que ele não consegue ver. Marcar resolve as duas coisas: ele acha e reativa.
 */
export function Linha({ inativo, children }: { inativo: boolean; children: ReactNode }) {
  return (
    <tr
      className={cn(
        'border-b border-border/60 transition-colors last:border-0 hover:bg-accent/40',
        inativo && 'bg-muted/30 text-muted-foreground',
      )}
    >
      {children}
    </tr>
  );
}

// ── etiquetas ──────────────────────────────────────────────────────────────

/** Estado em cor **e** em palavra: cor sozinha não comunica para daltônicos. */
export function EtiquetaDeAtividade({ ativo }: { ativo: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        ativo
          ? 'bg-success/12 text-success ring-success/25'
          : 'bg-muted text-muted-foreground ring-border',
      )}
    >
      {ativo ? 'Ativa' : 'Inativa'}
    </span>
  );
}

export function Chave({ valor }: { valor: string | null }) {
  if (!valor) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <code
      title="A chave não muda depois de criada: integração, URL e automação a usam."
      className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
    >
      {valor}
    </code>
  );
}

/** "3 chamados" — o número que a confirmação de desativação vai repetir. */
export function ContagemDeUso({ uso }: { uso: CatalogUsage }) {
  if (uso.tickets === 0) {
    return <span className="text-xs text-muted-foreground">nenhum</span>;
  }
  return (
    <span className="text-xs tabular-nums">
      {uso.tickets} {uso.tickets === 1 ? 'chamado' : 'chamados'}
    </span>
  );
}

// ── reordenação ────────────────────────────────────────────────────────────

/**
 * Subir e descer em vez de arrastar.
 *
 * Arrastar não tem equivalente de teclado sem um bocado de código, e esta é
 * uma tela de configuração operada por poucas pessoas com listas de dezenas de
 * linhas. Dois botões resolvem, funcionam no teclado e no leitor de tela, e não
 * trazem dependência nova.
 */
export function BotoesDeOrdem({
  primeira,
  ultima,
  ocupado,
  aoSubir,
  aoDescer,
}: {
  primeira: boolean;
  ultima: boolean;
  ocupado: boolean;
  aoSubir: () => void;
  aoDescer: () => void;
}) {
  return (
    <div className="flex items-center gap-0.5">
      <BotaoDeIcone
        rotulo="Subir"
        desabilitado={primeira || ocupado}
        aoClicar={aoSubir}
        icone={<ChevronUp className="h-4 w-4" aria-hidden />}
      />
      <BotaoDeIcone
        rotulo="Descer"
        desabilitado={ultima || ocupado}
        aoClicar={aoDescer}
        icone={<ChevronDown className="h-4 w-4" aria-hidden />}
      />
    </div>
  );
}

export function BotaoDeIcone({
  rotulo,
  icone,
  aoClicar,
  desabilitado = false,
  tom = 'neutro',
}: {
  rotulo: string;
  icone: ReactNode;
  aoClicar: () => void;
  desabilitado?: boolean;
  tom?: 'neutro' | 'perigo';
}) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      disabled={desabilitado}
      title={rotulo}
      aria-label={rotulo}
      className={cn(
        'grid h-8 w-8 place-items-center rounded-md transition-colors',
        'focus-visible:shadow-glow focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-30',
        tom === 'perigo'
          ? 'text-muted-foreground hover:bg-destructive/10 hover:text-destructive'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {icone}
    </button>
  );
}

// ── ações da linha ─────────────────────────────────────────────────────────

export function AcoesDaLinha({
  registro,
  entidade,
  ocupado,
  aoEditar,
  aoAlternarAtividade,
  aoRemover,
}: {
  registro: CatalogEntry;
  entidade: string;
  ocupado: boolean;
  aoEditar: () => void;
  aoAlternarAtividade: () => void;
  aoRemover: () => void;
}) {
  const emUso = registro.usage.tickets > 0 || registro.usage.children > 0;

  return (
    <div className="flex items-center justify-end gap-0.5">
      <BotaoDeIcone
        rotulo={`Editar ${entidade}`}
        aoClicar={aoEditar}
        desabilitado={ocupado}
        icone={<Pencil className="h-4 w-4" aria-hidden />}
      />
      <BotaoDeIcone
        rotulo={registro.active ? `Desativar ${entidade}` : `Reativar ${entidade}`}
        aoClicar={aoAlternarAtividade}
        desabilitado={ocupado}
        icone={
          registro.active ? (
            <Power className="h-4 w-4" aria-hidden />
          ) : (
            <RotateCcw className="h-4 w-4" aria-hidden />
          )
        }
      />
      <BotaoDeIcone
        rotulo={
          emUso
            ? `Não dá para remover: ${registro.usage.tickets} chamado(s) usam esta ${entidade}. Desative.`
            : `Remover ${entidade}`
        }
        tom="perigo"
        aoClicar={aoRemover}
        desabilitado={ocupado || emUso}
        icone={<Trash2 className="h-4 w-4" aria-hidden />}
      />
    </div>
  );
}

// ── confirmação ────────────────────────────────────────────────────────────

export type Intencao = 'desativar' | 'remover';

/**
 * Confirmação que **diz o tamanho do estrago antes de acontecer**.
 *
 * A contagem de chamados não é enfeite: desativar uma situação usada por 300
 * chamados e desativar uma que ninguém usou são decisões diferentes, e quem
 * confirma precisa saber qual das duas está tomando. O número vem da mesma
 * listagem que desenhou a linha, então não há uma segunda ida ao servidor
 * entre o clique e o texto.
 */
export function ConfirmacaoDeCatalogo({
  aberto,
  intencao,
  entidade,
  registro,
  erro,
  ocupado,
  aoFechar,
  aoConfirmar,
}: {
  aberto: boolean;
  intencao: Intencao;
  entidade: string;
  registro: CatalogEntry | null;
  erro: string | null;
  ocupado: boolean;
  aoFechar: () => void;
  aoConfirmar: () => void;
}) {
  if (!registro) return null;

  const { tickets, children } = registro.usage;
  const desativar = intencao === 'desativar';

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`${desativar ? 'Desativar' : 'Remover'} "${registro.name}"?`}
      descricao={desativar ? undefined : 'Esta ação não tem desfazer pela tela.'}
    >
      <div className="space-y-4 px-5 py-4 text-sm">
        <p>
          {tickets === 0 ? (
            <>
              Nenhum chamado usa a {entidade} <strong className="font-semibold">{registro.name}</strong>.
            </>
          ) : (
            <>
              <strong className="font-semibold tabular-nums">{tickets}</strong>{' '}
              {tickets === 1 ? 'chamado usa' : 'chamados usam'} a {entidade}{' '}
              <strong className="font-semibold">{registro.name}</strong>.
            </>
          )}
        </p>

        {children > 0 ? (
          <p className="text-muted-foreground">
            Também há <strong className="font-semibold tabular-nums">{children}</strong>{' '}
            {children === 1 ? 'registro dependente' : 'registros dependentes'} pendurados nela.
          </p>
        ) : null}

        <p className="text-muted-foreground">
          {desativar
            ? 'Desativar tira a linha dos seletores de abertura e edição. Os chamados que já apontam para ela continuam mostrando o nome — nada some do histórico, e reativar é um clique.'
            : 'Remover só é possível porque nada aponta para esta linha. A chave, no entanto, continua reservada: automação escrita para ela não pode passar a encontrar outro registro.'}
        </p>

        {erro ? (
          <p role="alert" className="text-sm text-destructive">
            {erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant={desativar ? 'primary' : 'danger'}
            loading={ocupado}
            onClick={aoConfirmar}
          >
            {desativar ? 'Desativar' : 'Remover'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── formulário ─────────────────────────────────────────────────────────────

export const CLASSE_DE_CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors placeholder:text-muted-foreground ' +
  'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow ' +
  'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground';

export function Campo({
  id,
  rotulo,
  dica,
  opcional = false,
  children,
}: {
  id: string;
  rotulo: string;
  dica?: string;
  opcional?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="flex items-baseline gap-1.5 text-sm font-medium">
        {rotulo}
        {opcional ? (
          <span className="text-xs font-normal text-muted-foreground">opcional</span>
        ) : null}
      </label>
      {children}
      {dica ? <p className="text-xs text-muted-foreground">{dica}</p> : null}
    </div>
  );
}

/**
 * Sugere a chave a partir do nome, enquanto ninguém a tiver digitado.
 *
 * A chave é para sempre, e digitá-la à mão é onde nascem `Rede`, `rede_2` e
 * `rede-nova`. Sugerir a partir do nome dá um padrão; o campo continua
 * editável, porque quem está importando de outro sistema precisa casar com a
 * chave de lá.
 */
export function chaveSugerida(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// ── mutação ────────────────────────────────────────────────────────────────

/**
 * Envolve uma escrita de catálogo: invalida a raiz e guarda a mensagem de erro.
 *
 * Invalidar a raiz inteira, e não a chave do catálogo mexido, é deliberado —
 * desativar uma categoria muda a contagem de uso das subcategorias dela e o
 * vocabulário que a tela de chamado lê.
 */
export function useEscritaDeCatalogo<TEntrada = void, TSaida = unknown>(
  escrever: (entrada: TEntrada) => Promise<TSaida>,
  aoConcluir?: () => void,
) {
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const mutacao = useMutation<TSaida, Error, TEntrada>({
    mutationFn: escrever,
    onMutate: () => setErro(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeAdministracao.raiz });
      // As telas de chamado leem o mesmo vocabulário por `/lookups`: sem isto,
      // a categoria recém-criada só apareceria no formulário meia hora depois.
      void cliente.invalidateQueries({ queryKey: ['apoio'] });
      aoConcluir?.();
    },
    onError: (falha) => setErro(mensagemDeErro(falha) ?? 'Não foi possível salvar.'),
  });

  return { mutacao, erro, limparErro: () => setErro(null) };
}
